import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import axios from 'axios'
import {
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid'
import { AddScraper } from './AddScraperBtn'
import StatusSelect from './StatusSelect'
import DataTableShell from '../../../../Components/table/DataTableShell'
import TableToolbar from '../../../../Components/table/TableToolbar'
import TableSearch from '../../../../Components/table/TableSearch'
import {
  TableActionGroup,
  TableEmptyRow,
  TableIdCell,
  TableStatusChip,
} from '../../../../Components/table/TableCells'
import useClientTable from '../../../../Components/table/useClientTable'

const TABLE_HEAD = ['Identifier', 'Category', 'Scraper', 'Status', 'Scrape by', 'Actions']
const filterScraper = (item, query) =>
  !query ||
  item.scraperId?.toString().toLowerCase().includes(query) ||
  item.status?.toString().toLowerCase().includes(query) ||
  item.name?.toString().toLowerCase().includes(query) ||
  item.category?.toString().toLowerCase().includes(query)
const sortScrapers = (a, b) => String(a.scraperId || '').localeCompare(String(b.scraperId || ''))

export function BrandScrapersTable() {
  const [scrapersData, setScrapersData] = useState([])
  const [isAltered, setIsAltered] = useState(false)
  const [callBackState, setCallBackState] = useState(false)
  const [onEdit, setOnEdit] = useState(false)
  const [scraperUpdate, setScraperUpdate] = useState({ status: '' })

  const brandsPerPage = 10
  const {
    searchTerm,
    handleSearch,
    currentPage,
    setCurrentPage,
    pageItems: brandScraperArr,
    totalPages,
    totalFiltered,
    startIndex,
  } = useClientTable({
    data: scrapersData,
    pageSize: brandsPerPage,
    filterFn: filterScraper,
    sortFn: sortScrapers,
  })
  const activeCount = scrapersData.filter((item) => item.status === 'Active').length

  useEffect(() => {
    axios
      .get('/api/webscrapers/all/scrapers')
      .then((response) => {
        if (response.data.message) {
          toast.error('Scraper inventory could not be loaded.')
        } else {
          setScrapersData(response.data.scrapers)
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        toast.error('Scraper inventory could not be loaded.')
      })
  }, [isAltered, callBackState])

  const handleCallBackState = () => setCallBackState((previous) => !previous)

  const toggleOnEdit = (index = false) => {
    setOnEdit((previous) => (previous === index ? false : index))
    setScraperUpdate({ status: '' })
  }

  const handleStatusChange = (newStatus) => {
    setScraperUpdate({ status: newStatus })
  }

  const handleUpdateScraper = (scraperId, currentStatus) => {
    axios
      .put(`/api/webscrapers/update/${scraperId}`, {
        status: scraperUpdate.status || currentStatus,
      })
      .then((response) => {
        if (response.data?.message) {
          toast.success(response.data.message)
        } else {
          toast.error('The scraper was not updated.')
        }
        setIsAltered((previous) => !previous)
        setOnEdit(false)
      })
      .catch((error) => {
        toast.error('The scraper was not updated.')
        console.error('Error:', error)
      })
  }

  const handleDeleteScraper = (scraperId) => {
    const isConfirmed = window.confirm(`Delete scraper ${scraperId}?`)
    if (!isConfirmed) return

    axios
      .delete(`/api/webscrapers/delete/${scraperId}`)
      .then((response) => {
        if (response.data?.message) {
          toast.success(response.data.message)
        } else {
          toast.error('The scraper was not deleted.')
        }
        setIsAltered((previous) => !previous)
      })
      .catch((error) => {
        toast.error('The scraper was not deleted.')
        console.error('Error:', error)
      })
  }

  return (
    <DataTableShell
      minWidth="780px"
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      totalPages={totalPages}
      toolbar={
        <TableToolbar
          kicker="Inventory"
          title="Registered scrapers"
          subtitle={`${activeCount} active · ${totalFiltered} matching · ${scrapersData.length} total`}
          actions={<AddScraper handleCallBackState={handleCallBackState} />}
        >
          <TableSearch
            id="scraper-inventory-search"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search inventory"
          />
        </TableToolbar>
      }
    >
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => (
                <th key={head}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {brandScraperArr.length === 0 ? (
              <TableEmptyRow
                columns={TABLE_HEAD.length}
                title="No scrapers found"
                description="Try a different identifier, category, name, or status."
              />
            ) : (
              brandScraperArr.map((item, index) => {
                const globalIndex = startIndex + index
                const isEditing = onEdit === globalIndex

                return (
                  <tr key={item.scraperId}>
                    <td><TableIdCell>{item.scraperId}</TableIdCell></td>
                    <td>
                      <span className="nx-chip-idle uppercase">{item.category}</span>
                    </td>
                    <td className="font-medium">{item.name}</td>
                    <td>
                      {isEditing ? (
                        <StatusSelect
                          status={item.status}
                          onStatusChange={handleStatusChange}
                        />
                      ) : (
                        <TableStatusChip status={item.status} />
                      )}
                    </td>
                    <td className="text-muted">{item.scrapeBy}</td>
                    <td>
                      <TableActionGroup>
                        {isEditing ? (
                          <>

                            <button
                              type="button"
                              className="nx-btn-ghost p-2 text-success"
                              onClick={() =>
                                handleUpdateScraper(item.scraperId, item.status)
                              }
                              title="Save status"
                            >
                              <CheckIcon className="h-4 w-4" />
                              <span className="sr-only">Save status</span>
                            </button>
                            <button
                              type="button"
                              className="nx-btn-ghost p-2 text-muted"
                              onClick={() => toggleOnEdit(globalIndex)}
                              title="Cancel editing"
                            >
                              <XMarkIcon className="h-4 w-4" />
                              <span className="sr-only">Cancel editing</span>
                            </button>
                          </>
                        ) : (
                          <>

                            <button
                              type="button"
                              className="nx-btn-ghost p-2 text-signal"
                              onClick={() => toggleOnEdit(globalIndex)}
                              title="Edit status"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                              <span className="sr-only">Edit status</span>
                            </button>
                            <button
                              type="button"
                              className="nx-btn-ghost p-2 text-danger"
                              onClick={() => handleDeleteScraper(item.scraperId)}
                              title="Delete scraper"
                            >
                              <TrashIcon className="h-4 w-4" />
                              <span className="sr-only">Delete scraper</span>
                            </button>
                          </>
                        )}
                      </TableActionGroup>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
    </DataTableShell>
  )
}
